/**
 * Extended (non-v5) benefit fields, per LOC.
 *
 * Extended fields are the analyst-only fields that persist under
 * `benefits.extended` rather than in the v5 carrier-facing body (ADR 10.32).
 * The per-LOC transforms decide which grid keys route there; these sets name
 * the same keys so the grid can tell an extended cell from a v5 one without
 * running a transform.
 *
 * PPX uses this to make extended fields — and only extended fields — editable
 * on the RFP-requested plan in the Quotes side-by-side (CD-2301). Anything a
 * transform does not route into `extended` stays read-only there.
 */

import {
  ACCIDENT_EXTENDED_GRID_FIELD_KEYS,
  BASIC_LIFE_EXTENDED_GRID_FIELD_KEYS,
  CANCER_EXTENDED_GRID_FIELD_KEYS,
  CRITICAL_ILLNESS_EXTENDED_GRID_FIELD_KEYS,
  DENTAL_PPO_EXTENDED_GRID_FIELD_KEYS,
  EAP_EXTENDED_GRID_FIELD_KEYS,
  HOSPITAL_INDEMNITY_EXTENDED_GRID_FIELD_KEYS,
  LTD_EXTENDED_GRID_FIELD_KEYS,
  STD_EXTENDED_GRID_FIELD_KEYS,
  VISION_EXTENDED_GRID_FIELD_KEYS,
  VOL_LIFE_EXTENDED_GRID_FIELD_KEYS,
  WHOLE_LIFE_EXTENDED_GRID_FIELD_KEYS,
} from "@platform/btx-benefit-transforms";
import { CoverageTypeEnum } from "@platform/types";

import { DENTAL_HMO_EXTENDED_GRID_FIELD_KEYS } from "../locs/dental-hmo";

import type { LocCoverageType } from "../locs/locMap";

const NO_EXTENDED_FIELDS: ReadonlySet<string> = new Set<string>();

/**
 * FMLA and Individual Disability have no extended fields, so they are absent
 * here and resolve to the empty set.
 */
const EXTENDED_GRID_FIELD_KEYS_BY_COVERAGE: Partial<
  Record<LocCoverageType, ReadonlySet<string>>
> = {
  [CoverageTypeEnum.ACCIDENT]: ACCIDENT_EXTENDED_GRID_FIELD_KEYS,
  [CoverageTypeEnum.BASIC_LIFE_ADND]: BASIC_LIFE_EXTENDED_GRID_FIELD_KEYS,
  [CoverageTypeEnum.CANCER]: CANCER_EXTENDED_GRID_FIELD_KEYS,
  [CoverageTypeEnum.CRITICAL_ILLNESS]:
    CRITICAL_ILLNESS_EXTENDED_GRID_FIELD_KEYS,
  [CoverageTypeEnum.DENTAL_HMO]: DENTAL_HMO_EXTENDED_GRID_FIELD_KEYS,
  [CoverageTypeEnum.DENTAL_PPO]: DENTAL_PPO_EXTENDED_GRID_FIELD_KEYS,
  [CoverageTypeEnum.EAP]: EAP_EXTENDED_GRID_FIELD_KEYS,
  [CoverageTypeEnum.HOSPITAL_INDEMNITY]:
    HOSPITAL_INDEMNITY_EXTENDED_GRID_FIELD_KEYS,
  [CoverageTypeEnum.LTD]: LTD_EXTENDED_GRID_FIELD_KEYS,
  [CoverageTypeEnum.STD]: STD_EXTENDED_GRID_FIELD_KEYS,
  [CoverageTypeEnum.VISION]: VISION_EXTENDED_GRID_FIELD_KEYS,
  [CoverageTypeEnum.VOLUNTARY_LIFE_ADND]: VOL_LIFE_EXTENDED_GRID_FIELD_KEYS,
  [CoverageTypeEnum.WHOLE_LIFE]: WHOLE_LIFE_EXTENDED_GRID_FIELD_KEYS,
};

/**
 * The grid keys of a coverage's extended (non-v5) fields. Empty for a LOC with
 * none, and for an unknown coverage type.
 */
export const getExtendedGridFieldKeys = (
  coverageType: LocCoverageType | undefined,
): ReadonlySet<string> =>
  (coverageType && EXTENDED_GRID_FIELD_KEYS_BY_COVERAGE[coverageType]) ||
  NO_EXTENDED_FIELDS;
